<script setup lang="ts">
import { ref, computed } from 'vue'
import { useLoginProfiles, type LoginProfile } from '@/composables/useLoginProfiles'
import { useAuthStore } from '@/stores/auth.store'

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'loginSuccess'): void
}>()

const authStore = useAuthStore()
const { profiles, selectedId, addProfile, updateProfile, removeProfile, markLoginSuccess, getSelected } = useLoginProfiles()

const loginError = ref('')
const loginLoading = ref(false)
const editPassword = ref('')  // 편집 중 비밀번호 (프로파일에 저장하지 않은 경우)
const showPassword = ref(false)

const selected = computed(() => getSelected())

function onSelectProfile(id: string) {
  selectedId.value = id
  loginError.value = ''
  // 프로파일의 저장된 비밀번호 로드
  const p = profiles.value.find(pp => pp.id === id)
  editPassword.value = p?.password || ''
}

function onAddProfile() {
  addProfile()
  editPassword.value = ''
  loginError.value = ''
}

function onRemoveProfile() {
  if (!selectedId.value) return
  if (profiles.value.length <= 1) return  // 최소 1개 유지
  removeProfile(selectedId.value)
  editPassword.value = ''
}

function onFieldChange(field: keyof LoginProfile, value: any) {
  if (!selectedId.value) return
  updateProfile(selectedId.value, { [field]: value })
}

async function onLogin() {
  const profile = selected.value
  if (!profile) return

  // 로컬 계정 인증으로 전환(안 A) — mobiusUrl 은 더 이상 필수가 아니다.
  if (!profile.email || !editPassword.value) {
    loginError.value = '이메일과 비밀번호를 입력하세요.'
    return
  }

  loginLoading.value = true
  loginError.value = ''

  try {
    const res = await fetch(`/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        // 앞뒤 공백을 제거한다 — 복사·붙여넣기·자동완성에서 딸려오는 흔한 실수다.
        // 2026-09-02 실측: 비밀번호 끝에 공백 한 칸이 붙어 로그인이 계속 실패했다.
        // 서버 검증은 정확 매칭 그대로 두고(끝 공백이 유효 문자일 수 있으므로)
        // 입력 단계에서만 정리한다 — 이 계정들은 끝 공백을 쓰지 않는다.
        email: profile.email.trim(),
        password: editPassword.value.trim(),
      }),
    })

    const data = await res.json()

    if (data.ok) {
      // 프로파일에 비밀번호 저장 (옵션)
      if (profile.savePassword) {
        updateProfile(profile.id, { password: editPassword.value })
      }
      markLoginSuccess(profile.id)

      authStore.setLogin({
        email: data.data.email,
        name: data.data.name,
        mobiusUrl: profile.mobiusUrl,
        apiKey: data.data.apiKey,
      })

      emit('loginSuccess')
      emit('close')
    } else {
      loginError.value = data.message || '로그인에 실패했습니다.'
    }
  } catch (err: any) {
    loginError.value = `서버 연결 실패: ${err.message}`
  } finally {
    loginLoading.value = false
  }
}
</script>

<template>
  <div class="login-backdrop" @click.self="emit('close')">
    <div class="login-dialog">
      <h2>관리자 로그인</h2>

      <div class="login-layout">
        <!-- 좌측: 프로파일 목록 -->
        <div class="profile-list">
          <div class="profile-list-header">
            <span>프로파일</span>
            <button class="btn-icon" @click="onAddProfile" title="추가">+</button>
          </div>
          <ul>
            <li
              v-for="p in profiles"
              :key="p.id"
              :class="{ selected: p.id === selectedId }"
              @click="onSelectProfile(p.id)"
            >
              <span class="profile-name">{{ p.name }}</span>
              <span class="profile-email">{{ p.email || '(미입력)' }}</span>
            </li>
          </ul>
        </div>

        <!-- 우측: 프로파일 편집 -->
        <div class="profile-edit" v-if="selected">
          <div class="form-field">
            <label>프로파일 이름</label>
            <input type="text" :value="selected.name" @input="onFieldChange('name', ($event.target as HTMLInputElement).value)" />
          </div>
          <div class="form-field">
            <label>Mobius 주소</label>
            <input type="text" :value="selected.mobiusUrl" @input="onFieldChange('mobiusUrl', ($event.target as HTMLInputElement).value)" placeholder="http://host:7579/Mobius" />
          </div>
          <div class="form-field">
            <label>이메일</label>
            <input type="email" :value="selected.email" @input="onFieldChange('email', ($event.target as HTMLInputElement).value)" />
          </div>
          <div class="form-field">
            <label>비밀번호</label>
            <div class="password-wrapper">
              <input :type="showPassword ? 'text' : 'password'" v-model="editPassword" @keydown.enter="onLogin" />
              <button type="button" class="btn-password-toggle" @click="showPassword = !showPassword" :title="showPassword ? '비밀번호 숨기기' : '비밀번호 보기'">
                {{ showPassword ? '◉' : '◎' }}
              </button>
            </div>
          </div>
          <div class="form-field checkbox">
            <label>
              <input type="checkbox" :checked="selected.savePassword" @change="onFieldChange('savePassword', ($event.target as HTMLInputElement).checked)" />
              비밀번호 저장
            </label>
          </div>

          <div v-if="loginError" class="login-error">{{ loginError }}</div>

          <div class="form-actions">
            <button class="btn btn-sm btn-danger" @click="onRemoveProfile" :disabled="profiles.length <= 1">삭제</button>
            <div class="spacer"></div>
            <button class="btn btn-sm btn-secondary" @click="emit('close')">취소</button>
            <button class="btn btn-sm btn-primary" @click="onLogin" :disabled="loginLoading">
              {{ loginLoading ? '로그인 중...' : '로그인' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.login-backdrop {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 1000;
  display: flex;
  justify-content: center;
  align-items: center;
}
.login-dialog {
  background: #1e1e1e;
  border: 1px solid #444;
  border-radius: 8px;
  padding: 20px;
  width: 560px;
  max-width: 90vw;
  color: #ddd;
}
.login-dialog h2 {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 600;
}
.login-layout {
  display: flex;
  gap: 16px;
}
.profile-list {
  width: 180px;
  flex-shrink: 0;
  border: 1px solid #444;
  border-radius: 4px;
  overflow: hidden;
}
.profile-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 8px;
  background: #2a2a2a;
  font-size: 12px;
  font-weight: 600;
}
.btn-icon {
  background: none;
  border: none;
  color: #aaa;
  font-size: 16px;
  cursor: pointer;
  padding: 0 4px;
}
.btn-icon:hover { color: #fff; }
.profile-list ul {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 200px;
  overflow-y: auto;
}
.profile-list li {
  padding: 6px 8px;
  cursor: pointer;
  border-bottom: 1px solid #333;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.profile-list li:hover { background: #333; }
.profile-list li.selected { background: #0d6efd33; border-left: 3px solid #0d6efd; }
.profile-name { font-size: 12px; font-weight: 500; }
.profile-email { font-size: 10px; color: #888; }

.profile-edit {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.form-field {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.form-field label {
  font-size: 11px;
  color: #999;
}
.form-field input[type="text"],
.form-field input[type="email"],
.form-field input[type="password"] {
  padding: 6px 8px;
  background: #2a2a2a;
  border: 1px solid #555;
  border-radius: 4px;
  color: #ddd;
  font-size: 13px;
}
.password-wrapper {
  display: flex;
  gap: 0;
}
.password-wrapper input {
  flex: 1;
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.btn-password-toggle {
  padding: 0 8px;
  background: #2a2a2a;
  border: 1px solid #555;
  border-left: none;
  border-radius: 0 4px 4px 0;
  color: #888;
  cursor: pointer;
  font-size: 14px;
}
.btn-password-toggle:hover {
  color: #ddd;
}
.form-field.checkbox label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  cursor: pointer;
}
.login-error {
  color: #dc3545;
  font-size: 12px;
  padding: 4px 0;
}
.form-actions {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}
.spacer { flex: 1; }
</style>
